import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import {
  selectRoomSession,
  selectRoomSessionIsLoading,
  selectRoomSessionMeetingTitle,
  selectRoomSessionMetadataUpdateIsLoading,
} from '../../../../../store/meetingSummary/roomSession'
import {
  selectMeetingTemplates,
  selectSelectedMeetingTemplate,
  selectSessionMeetingTemplate,
  setSelectedMeetingTemplate,
} from '../../../../../store/meetingSummary/meetingTemplates'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { TrackEvent, useTracking } from '../../common/tracking'
import { selectMeetingSummaryLoading } from '../../../../../store/meetingSummary/meetingSummary'
import { displaySidebar, showToast } from '../../../../../store/meetingSummary/display'
import { SidebarTitle, SidebarType } from '../Sidebar/models'
import { getRoomTrackingDetails } from '../../tracking'
import { selectCommentsAreLoading, selectFilteredComments, setCommentedAiResultId } from '../../../../../store/meetingSummary/meetingSupport'
import { CommentRelationType } from '../../../../../models/MeetingSupport'
import { selectCurrentUser } from '../../../../../store/meetingSummary/user'
import { getOrderedMeetingTemplatePresets, getPresetResultsByInputParameterHierarchy, parseMetadata } from '../utils'
import { AiResult } from '../../../../../models/AiResult'
import { selectAiFeatures } from '../../../../../store/meetingSummary/aiFeatures'
import { selectAiResultPollingEnabled, selectAiResults, selectAiResultsLoading } from '../../../../../store/meetingSummary/aiResults'
import { useCopyData } from '../hooks'
import { useZocksAuth } from '../../../../../components/ScopedBlock/hooks'
import { useUpdateRoomSessionMetadata } from '../hooks/api'
import { formatDateDDMMYYYY, formatDateHM, getUserDisplayName } from '../../common/aifeatures/utils'
import { copyResult, getEmptyResultCopyContent } from '../copy'
import { selectInputParametersHierarchyForSelectedMeetingTemplate } from '../../../../../store/meetingSummary/inputParametersHierarchy'
import { removeTopLevelEmptyParagraphs } from '../MeetingResults/Lexical/utils'
import { useLazyGetTranscriptionFileLocationQuery } from '../../../../../store/media/media'
import { useLazyDownloadFileQuery } from '../../../../../store/media/file'
import { TranscriptionResult, TranscriptionResultResponse } from '../../../../../models/transcription'
import { useGetAccountQuery } from '../../../../../store/accountUserSettings/accountUserSettings'
import { selectExecutedAiFeaturesPollingEnabled } from '../../../../../store/meetingSummary/executedAiFeatures'
import { selectMeetingSummaryAgentLoading, selectMeetingSummaryAgentPollingEnabled } from '../../../../../store/meetingSummaryAgent/meetingSummaryAgent'

import { extendCopyContentWithComplianceMessage, HTML_TYPE, TEXT_TYPE, writeCopyContentToClipboard } from '../../../../../common/copy'
import { useEffectiveConfig } from '../../../../../common/useEffectiveConfig'
import { IntegrationServiceId } from '../../../../../common/integrations'

export const useHeaderMeetingTemplateSelector = () => {
  const dispatch = useAppDispatch()

  const roomSession = useAppSelector(selectRoomSession)
  const meetingTemplates = useAppSelector(selectMeetingTemplates)
  const selectedMeetingTemplate = useAppSelector(selectSelectedMeetingTemplate)
  const sessionMeetingTemplate = useAppSelector(selectSessionMeetingTemplate)

  useEffect(() => {
    if (!selectedMeetingTemplate && roomSession && sessionMeetingTemplate) {
      dispatch(setSelectedMeetingTemplate(sessionMeetingTemplate.id))
    }
  }, [dispatch, selectedMeetingTemplate, roomSession, sessionMeetingTemplate])

  return { meetingTemplates, sessionMeetingTemplate, selectedMeetingTemplate }
}

export const useHeaderShareButton = () => {
  const dispatch = useAppDispatch()

  const { tracked } = useTracking()

  const roomSession = useAppSelector(selectRoomSession)

  const _handleShare = async () => {
    await navigator.clipboard.writeText(`${window.location.href}`)
    dispatch(showToast({ variant: 'success', icon: 'bi-check-circle-fill', message: 'Meeting link copied to clipboard!' }))
  }

  const handleShare = tracked(_handleShare, {
    eventName: TrackEvent.AI_FEATURE_MANUAL_RUN,
    eventDetails: getRoomTrackingDetails(roomSession),
  })

  return { handleShare }
}

export const useHeaderNotepadButton = () => {
  const dispatch = useAppDispatch()

  const { tracked } = useTracking()

  const isLoading = useAppSelector(selectMeetingSummaryLoading)
  const roomSession = useAppSelector(selectRoomSession)

  const _handleOpenNotepad = () => {
    dispatch(displaySidebar({ type: SidebarType.NOTEPAD, title: SidebarTitle.NOTEPAD, icon: 'bi-journal-text' }))
  }

  const handleOpenNotepad = tracked(_handleOpenNotepad, {
    eventName: TrackEvent.NOTEPAD_OPENED,
    eventDetails: { ...getRoomTrackingDetails(roomSession) },
  })

  return { isLoading, handleOpenNotepad }
}

export const useHeaderExternalUpdatesButton = () => {
  const dispatch = useAppDispatch()

  const { tracked } = useTracking()

  const isLoading = useAppSelector(selectMeetingSummaryLoading)
  const roomSession = useAppSelector(selectRoomSession)

  const _handleOpenExternalUpdates = () => {
    dispatch(displaySidebar({ type: SidebarType.EXTERNAL_UPDATES, title: SidebarTitle.EXTERNAL_UPDATES, icon: 'bi-arrow-repeat' }))
  }

  const handleOpenExternalUpdates = tracked(_handleOpenExternalUpdates, {
    eventName: TrackEvent.EXTERNAL_UPDATES_OPENED,
    eventDetails: { ...getRoomTrackingDetails(roomSession) },
  })

  return { isLoading, handleOpenExternalUpdates }
}

export const useHeaderCommentsButton = () => {
  const dispatch = useAppDispatch()

  const { tracked } = useTracking()

  const comments = useAppSelector(selectFilteredComments)
  const isLoading = useAppSelector(selectCommentsAreLoading)
  const roomSession = useAppSelector(selectRoomSession)

  const commentCount = useMemo(() => {
    return comments.filter(({ relationType }) => relationType === CommentRelationType.AI_RESULT).length
  }, [comments])

  const _handleOpenComments = () => {
    dispatch(setCommentedAiResultId(null))
    dispatch(displaySidebar({ type: SidebarType.COMMENTS, title: SidebarTitle.COMMENTS, icon: 'bi-chat-square-text' }))
  }

  const handleOpenComments = tracked(_handleOpenComments, {
    eventName: TrackEvent.COMMENTS_OPENED,
    eventDetails: { ...getRoomTrackingDetails(roomSession) },
  })

  return { isLoading, commentCount, handleOpenComments }
}

export const useHeaderCRMExport = () => {
  const currentUser = useAppSelector(selectCurrentUser)
  const roomSession = useAppSelector(selectRoomSession)
  const selectedMeetingTemplate = useAppSelector(selectSelectedMeetingTemplate)

  const [showAddMeetingToCRMModal, setShowAddMeetingToCRMModal] = useState<boolean>(false)
  const [selectedCRMServiceId, setSelectedCRMServiceId] = useState<IntegrationServiceId | null>(null)

  const handleShowCRMModal = (serviceId: IntegrationServiceId) => {
    setSelectedCRMServiceId(serviceId)
    setShowAddMeetingToCRMModal(true)
  }

  const handleHideCRMModal = () => {
    setSelectedCRMServiceId(null)
    setShowAddMeetingToCRMModal(false)
  }

  return {
    currentUser,
    roomSession,
    selectedMeetingTemplate,
    showAddMeetingToCRMModal,
    selectedCRMServiceId,
    handleShowCRMModal,
    handleHideCRMModal,
  }
}

export const useProcessMeeting = () => {
  const dispatch = useAppDispatch()

  const { tracked } = useTracking()

  const roomSession = useAppSelector(selectRoomSession)
  const isLoading = useAppSelector(selectRoomSessionIsLoading)
  const isMetadataUpdateLoading = useAppSelector(selectRoomSessionMetadataUpdateIsLoading)

  const updateMetadata = useUpdateRoomSessionMetadata()

  const [displayProcessed, setDisplayProcessed] = useState<boolean>(false)

  const processed = useMemo(() => {
    if (!roomSession) {
      return false
    }
    const metadata = parseMetadata(roomSession.metadata)
    return metadata.external?.processed ?? false
  }, [roomSession])

  useEffect(() => {
    setDisplayProcessed(processed)
  }, [processed])

  const _handleSwitchMeetingProcessed = async () => {
    if (!roomSession) {
      return
    }

    setDisplayProcessed(!processed)
    const currentMetadata = parseMetadata(roomSession.metadata)
    const { external } = currentMetadata
    await updateMetadata({ ...external, processed: !processed })
    dispatch(showToast({ variant: 'success', icon: 'bi-check-circle-fill', message: 'Meeting updated successfully.' }))
  }

  const handleSwitchMeetingProcessed = tracked(
    _handleSwitchMeetingProcessed,
    {
      eventDetails: getRoomTrackingDetails(roomSession),
    },
    {
      eventNameExtractor: () => {
        return processed ? TrackEvent.ROOM_SESSION_UNPROCESSED : TrackEvent.ROOM_SESSION_PROCESSED
      },
    },
  )

  return {
    processed: displayProcessed,
    handleSwitchMeetingProcessed,
    isLoading: isLoading || isMetadataUpdateLoading,
  }
}

export const useHeaderCopyAllResults = () => {
  const dispatch = useAppDispatch()

  const roomSession = useAppSelector(selectRoomSession)
  const selectedMeetingTemplate = useAppSelector(selectSelectedMeetingTemplate)
  const aiFeatures = useAppSelector(selectAiFeatures)
  const aiResults = useAppSelector(selectAiResults)
  const meetingTitle = useAppSelector(selectRoomSessionMeetingTitle)
  const inputParametersHierarchy = useAppSelector(selectInputParametersHierarchyForSelectedMeetingTemplate)

  const { copyComplianceMessage, accountIsFetching } = useEffectiveConfig()

  const copyData = useCopyData()

  const { tracked } = useTracking()

  const _handleCopyAll = async () => {
    if (!selectedMeetingTemplate || accountIsFetching) {
      return
    }

    const { presets: meetingTemplatePresets } = selectedMeetingTemplate

    const orderedPresets = getOrderedMeetingTemplatePresets(meetingTemplatePresets)
    const results = orderedPresets
      .map((preset) => {
        const { aiFeaturePresetId, aiFeatureId } = preset
        if (!aiFeatureId || !inputParametersHierarchy) {
          return []
        }
        const { inputParametersHierarchyNodeByPresets } = inputParametersHierarchy
        const presetAiResults = getPresetResultsByInputParameterHierarchy(aiFeaturePresetId, aiFeatureId, aiResults, inputParametersHierarchyNodeByPresets)
        return presetAiResults.filter((r) => r.status !== 'no_result')
      })
      .flat()
      .filter((aiResult): aiResult is AiResult => !!aiResult)

    const copiedResultsWithHeader = results.map((aiResult) => {
      const { aiFeatureId } = aiResult
      const aiFeature = aiFeatures.find((aiFeature) => aiFeature.id === aiFeatureId)
      if (!aiFeature) {
        return getEmptyResultCopyContent()
      }
      const header = `${aiFeature?.name}`
      const copiedResult = copyResult(aiFeature, aiResult, copyData)
      copiedResult[HTML_TYPE] = removeTopLevelEmptyParagraphs(copiedResult[HTML_TYPE])
      return {
        [TEXT_TYPE]: header + '\n\n' + copiedResult[TEXT_TYPE],
        [HTML_TYPE]: `<h2><u>${header}</u></h2>` + copiedResult[HTML_TYPE],
      }
    })

    const textMeetingInfo = roomSession
      ? `${meetingTitle}\n${formatDateDDMMYYYY(roomSession.startedAt)}\n${formatDateHM(roomSession.startedAt)} - ${
          roomSession.finishedAt ? formatDateHM(roomSession.finishedAt) : ''
        }\nParticipants:\n${roomSession.users
          .map((u) => {
            return `- ${getUserDisplayName(u)}\n`
          })
          .join('')}\n`
      : ''

    const meetingInfoHTML = roomSession
      ? `<p>
          <strong>${meetingTitle}</strong>
        <p>${formatDateDDMMYYYY(roomSession.startedAt)}</p>
        <p>
         ${formatDateHM(roomSession.startedAt)} - ${roomSession.finishedAt ? formatDateHM(roomSession.finishedAt) : ''}
        </p>
        <p>Participants:</p>
        <ul>
        ${roomSession.users
          .map((u) => {
            return `<li>${getUserDisplayName(u)}</li>`
          })
          .join('')}
        </ul>
      </p>`
      : ''

    const textContents = textMeetingInfo + copiedResultsWithHeader.map((copiedResult) => copiedResult[TEXT_TYPE]).join('\n\n')
    const htmlContents = meetingInfoHTML + copiedResultsWithHeader.map((copiedResult) => copiedResult[HTML_TYPE]).join('')

    const _copyContent = { [TEXT_TYPE]: textContents, [HTML_TYPE]: htmlContents }

    const copyContent = extendCopyContentWithComplianceMessage(_copyContent, copyComplianceMessage)

    await writeCopyContentToClipboard(copyContent)

    dispatch(showToast({ variant: 'success', icon: 'bi-check-circle-fill', message: 'Copied to clipboard!' }))
  }

  const handleCopyAll = tracked(_handleCopyAll, {
    eventName: TrackEvent.ALL_AI_RESULTS_COPIED,
    eventDetails: {
      ...getRoomTrackingDetails(roomSession),
    },
  })

  return { handleCopyAll }
}

export const useHeaderExtraOptions = () => {
  const accountId = useAppSelector(selectAccountId)
  const { data: account } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: true })
  const dispatch = useAppDispatch()

  const { hasScope } = useZocksAuth()
  const hasDeleteSessionScope =
    account &&
    (!account.data.effectiveConfig.restrictedSessionDelete
      ? hasScope('**', 'room_session:delete', ['room:admin', 'room_session:admin'])
      : !account.data.effectiveConfig.restrictedSessionDeleteGlobal && hasScope('**', 'room_session:admin_delete', []))

  const hasTrimSessionScope =
    account &&
    (!account.data.effectiveConfig.restrictedSessionTrimming
      ? hasScope('**', 'room_session:trim', ['room:admin', 'room_session:admin'])
      : !account.data.effectiveConfig.restrictedSessionTrimmingGlobal && hasScope('**', 'room_session:admin_trim', []))

  const [modal, setModal] = useState<'deleteMeeting' | 'trimMeeting' | null>(null)

  const closeModal = useCallback(() => {
    setModal(null)
  }, [setModal])

  const handleTrimMeeting = useCallback(() => {
    if (hasTrimSessionScope) {
      setModal('trimMeeting')
    } else {
      dispatch(showToast({ variant: 'warning', icon: 'bi-exclamation-triangle', message: 'You have no permissions to trim the meeting.' }))
    }
  }, [dispatch, hasTrimSessionScope])

  const handleDeleteMeeting = () => {
    if (hasDeleteSessionScope) {
      setModal('deleteMeeting')
    } else {
      dispatch(showToast({ variant: 'warning', icon: 'bi-exclamation-triangle', message: 'You have no permissions to delete the meeting.' }))
    }
  }

  return {
    hasDeleteSessionScope,
    hasTrimSessionScope,
    handleTrimMeeting,
    handleDeleteMeeting,
    modal,
    closeModal,
    setModal,
  }
}

export const useHeaderDownloadTranscript = () => {
  const accountId = useAppSelector(selectAccountId)
  const roomSession = useAppSelector(selectRoomSession)

  const [loading, setLoading] = useState<boolean>(false)

  const [triggerGetFileLocation] = useLazyGetTranscriptionFileLocationQuery()
  const [triggerDownloadFile] = useLazyDownloadFileQuery()

  const handleDownloadTranscript = async (): Promise<boolean> => {
    if (!roomSession) {
      return false
    }
    setLoading(true)
    try {
      const { roomId, id: sessionId } = roomSession
      const { location } = await triggerGetFileLocation({ accountId, roomId, sessionId }).unwrap()
      const { transcriptions } = (await triggerDownloadFile(location).unwrap()) as TranscriptionResultResponse
      const header = ['"Start Time","End Time","User ID","Username","Text"']

      const text: Array<string> = transcriptions.map((t) => toCSVLine(t))

      const fileContent = header.concat(text).join('\r\n')

      const blob = new Blob([fileContent], { type: 'text/csv' })
      const fileName = `${roomId}_${sessionId}`.replace(/\.json$/, '.csv')

      if ('showSaveFilePicker' in window) {
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const handle = await (window as any).showSaveFilePicker({
            suggestedName: fileName,
            types: [{ description: 'CSV File', accept: { 'text/csv': ['.csv'] } }],
          })
          const writable = await handle.createWritable()
          await writable.write(blob)
          await writable.close()
          return true
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (pickerError: any) {
          if (pickerError?.name === 'AbortError') {
            return false
          }
          throw pickerError
        }
      }

      const element = window.document.createElement('a')
      element.href = window.URL.createObjectURL(blob)
      element.download = fileName
      document.body.appendChild(element)
      element.click()
      document.body.removeChild(element)
      return true
    } catch (e) {
      console.error(e)
      return false
    } finally {
      setLoading(false)
    }
  }

  const toCSVLine = (transcription: TranscriptionResult) => {
    const from = new Date(transcription.startTime * 1000).toISOString()
    const participant = roomSession?.users.find((p) => p.id === transcription.participantId)
    const to = new Date(transcription.endTime * 1000).toISOString()
    return `"${from}","${to}","${transcription.participantId}","${participant?.name ?? ''}","${transcription.transcript.replaceAll('"', '""')}"`
  }

  return { downloadTranscriptLoading: loading, handleDownloadTranscript }
}

export const useMeetingContentIsLoading = () => {
  const isAiFeatureResultsLoading = useAppSelector(selectAiResultsLoading)
  const isAiResultsPolling = useAppSelector(selectAiResultPollingEnabled)
  const isExecutedAiFeaturesPolling = useAppSelector(selectExecutedAiFeaturesPollingEnabled)
  const isMeetingSummaryPolling = useAppSelector(selectMeetingSummaryAgentPollingEnabled)
  const isMeetingSummaryAgentLoading = useAppSelector(selectMeetingSummaryAgentLoading)
  return isAiFeatureResultsLoading || isAiResultsPolling || isExecutedAiFeaturesPolling || isMeetingSummaryPolling || isMeetingSummaryAgentLoading
}
